import React, { useEffect, useState } from "react";
import axios from "axios";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import { ADMIN_BASE_URL } from "../config/config";
import "../components/App.css";

const emptyFaq = { question: "", answer: "" };

const AdminFAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyFaq);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("care_admin_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchFaqs = async () => {
    try {
      const response = await axios.get(`${ADMIN_BASE_URL}/faqs`, {
        headers: getAuthHeaders(),
      });
      setFaqs(response.data || []);
    } catch (error) {
      toast.error("Failed to load FAQs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyFaq);
    setShowModal(true);
  };

  const openEdit = (faq) => {
    setEditingId(faq._id);
    setForm({ question: faq.question, answer: faq.answer });
    setShowModal(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      if (editingId) {
        await axios.put(`${ADMIN_BASE_URL}/faqs/${editingId}`, form, {
          headers: getAuthHeaders(),
        });
        toast.success("FAQ updated");
      } else {
        await axios.post(`${ADMIN_BASE_URL}/faqs`, form, {
          headers: getAuthHeaders(),
        });
        toast.success("FAQ created");
      }
      setShowModal(false);
      fetchFaqs();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to save FAQ");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    try {
      await axios.delete(`${ADMIN_BASE_URL}/faqs/${id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("FAQ deleted");
      fetchFaqs();
    } catch (error) {
      toast.error("Failed to delete FAQ");
    }
  };

  return (
    <div className="admin-faq-content">
        <div className="admin-faq-header">
          <h2>FAQs</h2>
          <button type="button" className="admin-faq-btn" onClick={openCreate}>
            Add New
          </button>
        </div>

        {loading ? (
          <p>Loading FAQs...</p>
        ) : (
          <div className="admin-faq-table-wrap">
            <table className="admin-faq-table">
              <thead>
                <tr>
                  <th>Question</th>
                  <th>Answer</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {faqs.length === 0 ? (
                  <tr>
                    <td colSpan="3">No FAQs yet.</td>
                  </tr>
                ) : (
                  faqs.map((faq) => (
                    <tr key={faq._id}>
                      <td>{faq.question}</td>
                      <td>{faq.answer}</td>
                      <td>
                        <div className="admin-faq-actions">
                          <button
                            type="button"
                            className="admin-faq-btn"
                            onClick={() => openEdit(faq)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="admin-faq-btn admin-faq-btn--danger"
                            onClick={() => handleDelete(faq._id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{editingId ? "Edit FAQ" : "Add FAQ"}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Question</Form.Label>
                <Form.Control
                  value={form.question}
                  onChange={(e) => setForm({ ...form, question: e.target.value })}
                  required
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Answer</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={form.answer}
                  onChange={(e) => setForm({ ...form, answer: e.target.value })}
                  required
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button variant="success" type="submit" disabled={submitting}>
                {submitting ? "Saving..." : "Save"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
    </div>
  );
};

export default AdminFAQ;
